import AssetCoverToast from "../../../../common/features/asset/AssetCoverToast.tsx";
import { FormattedMessage } from "react-intl";
import Toast from "../../../../common/core-ui/Toast.tsx";
import {
  isBridgeRedemption,
  Redemption,
} from "../../../../common/api/decimal/methods/redeem.ts";
import { ReactNode } from "react";

interface RedeemToastProps {
  books?: Redemption[];
  ok: boolean;
  code: string;
  onClose?: () => void;
  errorCode?: string;
}

const SuccessToast = ({
  onClose,
  title,
  vbid = "",
  caveat,
}: Pick<RedeemToastProps, "onClose"> & {
  title: ReactNode;
  vbid?: string;
  caveat?: ReactNode;
}) => {
  const Component = vbid ? AssetCoverToast : Toast;
  return (
    <Component onClose={onClose} type={"success"} title={title} vbid={vbid}>
      {caveat ? (
        caveat
      ) : (
        <FormattedMessage
          defaultMessage={"It may take a few moments for the item to appear."}
        />
      )}
    </Component>
  );
};

export default function RedeemToast({
  books,
  ok,
  code,
  onClose,
  errorCode,
}: RedeemToastProps) {
  if (ok && books) {
    const redeemedCount = books.length;
    if (redeemedCount === 1) {
      if (isBridgeRedemption(books[0])) {
        return (
          <SuccessToast
            title={
              <FormattedMessage defaultMessage="This item has been added to your account"></FormattedMessage>
            }
            onClose={onClose}
          />
        );
      } else if (books[0].kind === "subscription") {
        return (
          <SuccessToast
            title={
              <FormattedMessage
                defaultMessage="{title} successfully added to Explore"
                values={{ title: books[0].title }}
              />
            }
            onClose={onClose}
          />
        );
      } else {
        return (
          <SuccessToast
            title={
              <FormattedMessage
                defaultMessage="“{title}” Added to My Library"
                values={{ title: books[0].title }}
              />
            }
            onClose={onClose}
            vbid={books[0].isbn}
          />
        );
      }
    } else {
      // can only be > 1 but plural text retained to not confuse translators
      return (
        <SuccessToast
          title={
            <FormattedMessage
              defaultMessage="{redeemedCount, plural, =1 {Title} other {# Titles}} added to My Library"
              values={{ redeemedCount }}
            ></FormattedMessage>
          }
          caveat={
            <FormattedMessage
              defaultMessage={
                "It may take a few moments for the items to appear."
              }
            />
          }
          onClose={onClose}
        ></SuccessToast>
      );
    }
  } else {
    let title: ReactNode;
    let message: ReactNode;

    if (errorCode && ["470", "1006", "1007"].includes(errorCode)) {
      title = (
        <FormattedMessage defaultMessage="Code cannot be Redeemed at this time" />
      );
      message = (
        <FormattedMessage defaultMessage="Please try again soon. We apologize for the inconvenience." />
      );
    } else {
      title = (
        <FormattedMessage defaultMessage="Code Invalid or Already Redeemed" />
      );
      message = (
        <FormattedMessage
          defaultMessage="Code “{code}” is invalid or has already been redeemed."
          values={{ code }}
        />
      );
    }

    return (
      <Toast onClose={onClose} type="error" title={title}>
        {message}
        <div>
          <a
            href="https://support.vitalsource.com/hc/en-us/articles/28276806590231-Issues-Redeeming-a-Bookshelf-Code"
            target="_blank"
            rel="noopener noreferrer"
            className="vst-link"
          >
            <FormattedMessage defaultMessage="Learn More" />
          </a>
        </div>
      </Toast>
    );
  }
}
